import {
  ARM64_GPU_NOTE,
  findEffectiveSetting,
  isSnapshotArm64,
  isSnapshotDeployable,
  isSnapshotRoot,
  SETTING_KEYS,
  snapshotRootRef,
  sortDeployableSnapshots,
  useCreateEmulator,
  useEffectiveSettings,
  useEmulators,
  useSnapshots,
  useTenantGroups,
  type EffectiveSetting,
  type SnapshotWithRoot,
} from "../ctrl/hooks";
import {
  getSnapshotDisplayName,
  getSnapshotRelativeTime,
  getSnapshotShortSha,
  shortRef,
} from "../ctrl/snapshots";
import { Button, Modal, Switch, Text, TextInput } from "@repo/ui/core";
import { IconRocket } from "@repo/ui/icons";
import { CornerButton, EmptyLine, SkeletonRows, Tag, toast } from "@repo/ui/na";
import { useEffect, useMemo, useState } from "react";
import classes from "./deploy.module.scss";

/** The signed-in viewer: decides which groups an emulator can be shared with. */
export type DeployViewer = {
  isAdmin?: boolean;
  tenantId?: string;
  groups: string[];
  /** Matches emulator owners, to show usage against the per-user limit. */
  email?: string;
};

export type DeployModalProps = {
  opened: boolean;
  onClose: () => void;
  /** Snapshot name to preselect. */
  snapshot?: string;
  viewer: DeployViewer;
  /** Called with the new emulator's id once the deploy was accepted. */
  onDeployed?: (emulatorId: string) => void;
  /** Called with the rejected create request's error; the form shows it too. */
  onDeployFailed?: (error: unknown) => void;
};

/** Deploy flow: pick a snapshot, name it, choose sharing and GPU. */
export function DeployModal({ opened, onClose, snapshot, viewer, onDeployed, onDeployFailed }: DeployModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Deploy emulator" size={560}>
      {opened && (
        <DeployForm initial={snapshot} close={onClose} session={viewer} onDeployed={onDeployed} onDeployFailed={onDeployFailed} />
      )}
    </Modal>
  );
}

/** ctrl's 409 says which limit was hit, e.g. "limit reached: maximum 4 emulators per tenant". */
export function limitMessage(serverMessage?: string): string {
  const detail = serverMessage?.replace(/^limit reached:\s*/i, "").trim();
  return detail ? `Emulator limit reached (${detail}).` : "Emulator limit reached.";
}

const SOURCE_TEXT: Record<string, string> = {
  user: "your limit",
  group: "from your group",
  tenant: "tenant limit",
  system: "platform limit",
  default: "default limit",
};

/** Where an effective limit came from, in the viewer's terms. */
export function limitSource(s: EffectiveSetting["source"]): string {
  if (s?.scope_type === "group" && (s.scope_ids?.length ?? 0) > 1) return "from your groups";
  return SOURCE_TEXT[s?.scope_type ?? ""] ?? "limit";
}

/** The viewer's emulators against their per-user limit; hidden until both are known. */
function LimitUsage({ email }: { email?: string }) {
  const { data: settings } = useEffectiveSettings();
  const { data: emulators } = useEmulators();
  const limit = findEffectiveSetting(settings, SETTING_KEYS.emulatorsPerUser);
  if (!email || !emulators || typeof limit?.value !== "number") return null;

  const me = email.toLowerCase();
  const used = emulators.filter((e) => e.owner?.toLowerCase() === me).length;
  const max = limit.value;
  const full = max > 0 && used >= max;
  return (
    <Text size="xs" c={full ? "var(--na-warning)" : "var(--na-fg-muted)"} data-testid="limit-usage">
      {max === 0 ? `${used} emulator${used === 1 ? "" : "s"} · no per-user limit` : `${used} of ${max} emulators in use`} · {limitSource(limit.source)}
      {full && ". Delete one to deploy another."}
    </Text>
  );
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Root snapshots carry `Root · ref`; the API's `root_ref` may be absent on old ctrl. */
const rootTag = (s: SnapshotWithRoot) =>
  `Root · ${snapshotRootRef(s) ?? s.job_ref ?? "root"}`;

function DeployForm({
  initial,
  close,
  session,
  onDeployed,
  onDeployFailed,
}: {
  initial?: string;
  close: () => void;
  session: DeployViewer;
  onDeployed?: (emulatorId: string) => void;
  onDeployFailed?: (error: unknown) => void;
}) {
  const { data: snaps, isLoading } = useSnapshots();
  const { data: tenantGroups } = useTenantGroups(session.isAdmin ? session.tenantId : undefined);
  const { trigger, isMutating } = useCreateEmulator();

  // Deployable AAOS snapshots only, roots first.
  const options = useMemo(
    () => sortDeployableSnapshots(((snaps ?? []) as SnapshotWithRoot[]).filter(isSnapshotDeployable)),
    [snaps],
  );
  const [picked, setPicked] = useState<string | undefined>(initial);
  const snapshot = options.find((o) => o.name === picked) ?? options[0];
  const [name, setName] = useState("");
  const [gpu, setGpu] = useState(false);
  const gpuUnsupported = isSnapshotArm64(snapshot);
  // Clear a prior choice so it doesn't reappear when switching back to x86_64.
  useEffect(() => {
    if (gpuUnsupported) setGpu(false);
  }, [gpuUnsupported]);
  const groupChoices = session.isAdmin
    ? (tenantGroups ?? []).map((g) => g.name).filter((n): n is string => Boolean(n))
    : session.groups;
  const [groups, setGroups] = useState<string[]>(() => session.groups.slice(0, 1));
  const [error, setError] = useState<string | null>(null);

  // Name after what's being deployed: project and ref for CI builds, the root's
  // line (e.g. android-16) for roots that carry no project.
  const placeholder = snapshot
    ? slug(
        [snapshot.project?.split("/").pop(), snapshot.job_ref && shortRef(snapshot.job_ref).replace(/^!/, "mr-")]
          .filter(Boolean)
          .join("-") ||
          snapshotRootRef(snapshot) ||
          snapshot.name ||
          "",
      )
    : "";

  const submit = async () => {
    if (!snapshot?.name) return;
    setError(null);
    try {
      const res = await trigger({
        snapshot_name: snapshot.name,
        display_name: name.trim() || placeholder || undefined,
        groups: groups.length ? groups : undefined,
        gpu: (gpu && !gpuUnsupported) || undefined,
      });
      close();
      toast("Emulator deploying", name.trim() || placeholder, "info");
      const id = res.emulator_id ?? res.id;
      if (id) onDeployed?.(id);
    } catch (e) {
      onDeployFailed?.(e);
      const { status, data } = e as { status?: number; data?: { message?: string } };
      setError(
        status === 409
          ? `${limitMessage(data?.message)} Delete an existing emulator first.`
          : e instanceof Error
            ? e.message
            : "Deploy failed",
      );
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <div className={classes.fieldLabel}>Snapshot</div>
          <div role="radiogroup" aria-label="Snapshot" className={classes.radioList}>
            {options.map((o) => {
              const on = o.name === snapshot?.name;
              return (
                <button
                  key={o.name}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={classes.radioRow}
                  data-on={on || undefined}
                  onClick={() => setPicked(o.name)}
                >
                  <span className={classes.radioBox}>
                    <span />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className={classes.radioTitle}>{getSnapshotDisplayName(o)}</span>
                    <span className={classes.radioSub}>
                      {[getSnapshotShortSha(o), o.labels?.arch ?? "x86_64", getSnapshotRelativeTime(o)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  {isSnapshotRoot(o) && <Tag tone="sky">{rootTag(o)}</Tag>}
                </button>
              );
            })}
            {isLoading && <SkeletonRows rows={4} height={32} label="Loading snapshots" />}
            {!isLoading && options.length === 0 && <EmptyLine>No deployable snapshots</EmptyLine>}
          </div>
        </div>

        <TextInput
          label="Display name"
          placeholder={placeholder}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        {groupChoices.length > 0 && (
          <div>
            <div className={classes.fieldLabel}>
              Share with groups <span style={{ fontWeight: 400, color: "var(--na-fg-muted)" }}>· members get Operator access</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {groupChoices.map((g) => {
                const on = groups.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={on}
                    className={classes.chip}
                    onClick={() => setGroups((gs) => (on ? gs.filter((x) => x !== g) : [...gs, g]))}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Switch
          checked={gpu && !gpuUnsupported}
          onChange={(e) => setGpu(e.currentTarget.checked)}
          disabled={gpuUnsupported}
          label="GPU acceleration"
          description={gpuUnsupported ? ARM64_GPU_NOTE : "For rendering-heavy workloads. Takes longer to schedule."}
        />

        <LimitUsage email={session.email} />
      </div>

      <div className={classes.modalFoot}>
        {error ? (
          <Text size="xs" c="var(--na-danger-text)" style={{ flex: 1 }}>
            {error}
          </Text>
        ) : (
          <span style={{ flex: 1 }} />
        )}
        <Button onClick={close} h={32}>
          Cancel
        </Button>
        <CornerButton type="submit" size="sm" leftSection={<IconRocket size={16} />} disabled={!snapshot || isMutating}>
          Deploy
        </CornerButton>
      </div>
    </form>
  );
}
